import { useState, useMemo } from 'react';
import { Table, Text, TextInput, Group, Button, ActionIcon, Avatar, Badge, Card, Title, Tooltip } from '@mantine/core';
import { mockTeams, mockTeamMembers, Team, TeamMember } from '../../types/team';
import { MagnifyingGlassIcon, PencilIcon, TrashIcon, UserGroupIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

interface TeamDirectoryProps {
  onCreateTeam: () => void;
  onEditTeam: (team: Team) => void;
  onDeleteTeam: (teamId: string) => void;
}

export function TeamDirectory({ onCreateTeam, onEditTeam, onDeleteTeam }: TeamDirectoryProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Get team leader information
  const getTeamLeader = (leaderId: string): TeamMember | undefined => {
    return mockTeamMembers.find(member => member.id === leaderId);
  };

  // Get team members count
  const getTeamMembersCount = (memberIds: string[]): number => {
    return memberIds.length;
  };

  // Filter teams based on search query
  const filteredTeams = useMemo(() => {
    return mockTeams.filter(team => {
      const leaderName = getTeamLeader(team.leaderId)?.name || '';
      return (
        team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        leaderName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  }, [searchQuery]);

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group justify="space-between" mb="md">
        <Title order={2} size="h3">Team Directory</Title>
        <Button 
          leftSection={<UserGroupIcon width={16} />} 
          onClick={onCreateTeam}
        >
          Create New Team
        </Button>
      </Group>

      <TextInput
        placeholder="Search teams..."
        mb="md"
        leftSection={<MagnifyingGlassIcon width={16} />}
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.currentTarget.value)}
      />

      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Team Name</Table.Th>
            <Table.Th>Team Leader</Table.Th>
            <Table.Th>Members</Table.Th>
            <Table.Th>Last Updated</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filteredTeams.map((team) => {
            const leader = getTeamLeader(team.leaderId);
            const membersCount = getTeamMembersCount(team.members);

            return (
              <Table.Tr key={team.id}>
                <Table.Td>
                  <Group gap="sm">
                    <Text fw={500}>{team.name}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Group gap="sm">
                    <Avatar 
                      color="gray" 
                      radius="xl"
                      src={leader?.avatar}
                    >
                      {leader?.name.substring(0, 2)}
                    </Avatar>
                    <div>
                      <Text size="sm" fw={500}>{leader?.name}</Text>
                      <Text size="xs" c="dimmed">{leader?.role}</Text>
                    </div>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Badge>{membersCount} members</Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{new Date(team.updatedAt).toLocaleDateString()}</Text>
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  <Group gap="xs" justify="flex-end">
                    <Tooltip label="Edit team">
                      <ActionIcon 
                        variant="subtle" 
                        color="gray" 
                        onClick={() => onEditTeam(team)}
                      >
                        <PencilIcon width={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Delete team">
                      <ActionIcon 
                        variant="subtle" 
                        color="red" 
                        onClick={() => onDeleteTeam(team.id)}
                      >
                        <TrashIcon width={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Card>
  );
} 
